import { useEffect, useState } from 'react'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem, Alert, CircularProgress } from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { generateInvoiceFromOrder, generateInvoiceFromShipment, generateProforma } from '../../api/financiar'
import { listOrders } from '../../api/comenzi'
import { listShipments } from '../../api/logistica'
import { errorMessage } from '../../api/client'

type Mode = 'comanda' | 'expeditie' | 'proforma'

export default function GenerateInvoiceDialog({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (id: string) => void }) {
  const [mode, setMode] = useState<Mode>('comanda')
  const [sourceId, setSourceId] = useState('')
  const qc = useQueryClient()

  const { data: orders } = useQuery({ queryKey: ['comercial', 'comenzi'], queryFn: listOrders, enabled: open })
  const { data: shipments } = useQuery({ queryKey: ['logistica', 'expeditii'], queryFn: listShipments, enabled: open })

  useEffect(() => { if (open) { setMode('comanda'); setSourceId('') } }, [open])

  const eligibleOrders = (orders ?? []).filter((o) => o.status !== 'Draft' && o.status !== 'Cancelled')
  const eligibleShipments = (shipments ?? []).filter((s) => s.status === 'Livrata')

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = { issueDate: null, dueDate: null }
      if (mode === 'expeditie') return generateInvoiceFromShipment(sourceId, payload)
      if (mode === 'proforma') return generateProforma(sourceId, payload)
      return generateInvoiceFromOrder(sourceId, payload)
    },
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['financiar', 'facturi'] })
      onCreated(r.id)
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Generează factură</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField select label="Sursă" value={mode} onChange={(e) => { setMode(e.target.value as Mode); setSourceId('') }}>
            <MenuItem value="comanda">Comandă (facturare integrală)</MenuItem>
            <MenuItem value="expeditie">Expediție (facturare parțială)</MenuItem>
            <MenuItem value="proforma">Proformă din comandă</MenuItem>
          </TextField>
          {mode === 'expeditie' ? (
            <TextField select label="Expediție" value={sourceId} onChange={(e) => setSourceId(e.target.value)}>
              {eligibleShipments.map((s) => <MenuItem key={s.id} value={s.id}>{s.number} — comandă {s.orderNumber}</MenuItem>)}
            </TextField>
          ) : (
            <TextField select label="Comandă" value={sourceId} onChange={(e) => setSourceId(e.target.value)}>
              {eligibleOrders.map((o) => <MenuItem key={o.id} value={o.id}>{o.orderNumber} — {o.clientName}</MenuItem>)}
            </TextField>
          )}
          {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Renunță</Button>
        <Button variant="contained" disabled={!sourceId || mutation.isPending} onClick={() => mutation.mutate()}
          startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
          Generează
        </Button>
      </DialogActions>
    </Dialog>
  )
}
